import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ima-montaz-preview/artifacts/e8el7v59_Logo%20Imamontaz.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Tentang Perusahaan', path: '/tentang' },
  { name: 'Kapabilitas', path: '/kapabilitas' },
  { name: 'Produk & Layanan', path: '/produk-layanan' },
  { name: 'Sertifikasi', path: '/sertifikasi' },
  { name: 'Klien', path: '/klien' },
  { name: 'Kontak', path: '/kontak' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isHomePage = location.pathname === '/';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 h-20 ${
          isScrolled || !isHomePage
            ? 'bg-white shadow-lg'
            : 'bg-gradient-to-r from-[#001582] to-[#001ea7]'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
              <img 
                src={LOGO_URL} 
                alt="PT IMA MONTAZ TEKNINDO" 
                className="h-10 md:h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-3 py-2 font-medium text-sm transition-all group ${
                    location.pathname === link.path
                      ? isScrolled || !isHomePage
                        ? 'text-[#001582] font-semibold'
                        : 'text-white font-semibold'
                      : isScrolled || !isHomePage
                        ? 'text-[#001582] hover:text-[#001ea7]'
                        : 'text-white/90 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.name}
                  {/* Underline hover effect */}
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 transition-all duration-300 ${
                    location.pathname === link.path 
                      ? 'w-full bg-[#001ea7]' 
                      : 'w-0 group-hover:w-full'
                  } ${isScrolled || !isHomePage ? 'bg-[#001ea7]' : 'bg-white'}`} />
                </Link>
              ))}
            </div>

            {/* CTA Button & Mobile Menu Toggle */}
            <div className="flex items-center gap-3">
              <a 
                href="https://wa.me/6285210005588" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden md:block"
              >
                <Button 
                  className="bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold rounded-xl px-6 shadow-lg"
                  data-testid="navbar-cta"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Hubungi Kami
                </Button>
              </a>
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`xl:hidden p-2 rounded-lg transition-colors ${
                  isScrolled || !isHomePage
                    ? 'text-[#001582] hover:bg-slate-100'
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 xl:hidden"
            data-testid="mobile-menu"
          >
            <div 
              className="absolute inset-0 bg-black/50" 
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-2xl">
              <div className="pt-24 px-6 pb-8">
                {/* Mobile Logo */}
                <div className="flex justify-center mb-6">
                  <img 
                    src={LOGO_URL} 
                    alt="PT IMA MONTAZ TEKNINDO" 
                    className="h-12 w-auto"
                  />
                </div>
                <nav className="space-y-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                        location.pathname === link.path
                          ? 'bg-gradient-to-r from-[#001582] to-[#001ea7] text-white'
                          : 'text-[#001582] hover:bg-slate-100'
                      }`}
                      data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <a 
                    href="https://wa.me/6285210005588" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold rounded-xl">
                      <Phone className="w-4 h-4 mr-2" />
                      Hubungi Kami
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
